import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Bounds } from 'map-wald';
import * as proj4  from 'proj4';
import { VectorLayerDescriptor } from './data';

const IMAGE_WIDTH=2048;
const STATIC_WMS_PARAMETERS = {
  service:'WMS',
  request:'GetMap',
  layers:'',
  styles:'',
  format:'image/png',
  transparent:true,
  version:'1.1.1',
  srs:'EPSG:3857'
};

const DYNAMIC_WMS_PARAMETERS = [
  'cmap',
  'cmap_bounds',
  'expression'
];

export interface DownloadService {
  options?: {
    label:string;
    dashboard:string;
    orientation:string;
    pages:number;
  }[],
  label: string;
  method?: string;
  baseURL: string;
  extension: string;
  url?: string;
  filename?: string;
  parameters?: {[key:string]:string};
}

// width=256
// height=256
// srs=EPSG%3A3857
// bbox=15028131.257091936,5009377.085697314,20037508.342789244,10018754.171394628


@Component({
  selector: 'spatial-download-panel',
  template: `
    <p>Map extent for download</p>
    <div class="row">
      <div class="col-12">
        <div class="btn-group btn-group-toggle flex-sm-wrap tall-toggle"
            ngbRadioGroup
            name="spatialExtentMode"
            [(ngModel)]="extentMode"
            (ngModelChange)="extentModeChanged()">
          <label ngbButtonLabel [ngClass]="(extentMode===0)?'btn-primary':'btn-secondary'">
            <input ngbButton type="radio" [value]="0">
            Current map extent
          </label>
          <label ngbButtonLabel [ngClass]="(extentMode===1)?'btn-primary':'btn-secondary'">
            <input ngbButton [disabled]="!selectedFeature" type="radio" [value]="1">
            Selected region
          </label>
          <label ngbButtonLabel [ngClass]="(extentMode===2)?'btn-primary':'btn-secondary'">
            <input ngbButton type="radio" [value]="2">
            Custom bounds
          </label>
        </div>
      </div>
    </div>
    <bounds-control *ngIf="extentMode===0" [bounds]="currentBounds" [readonly]="true"></bounds-control>
    <bounds-control *ngIf="extentMode===1" [bounds]="effectiveBounds" [readonly]="true"></bounds-control>
    <bounds-control *ngIf="extentMode===2" [bounds]="customBounds" (boundsChanged)="customBoundsUpdated($event)"></bounds-control>
    <p>File format</p>
    <div *ngIf="currentDownloadService" class="btn-group me-3">
      <select class="form-select" 
              [(ngModel)]="currentDownloadService"
              (ngModelChange)="download(currentDownloadService)">
        <option *ngFor="let downloadService of activeDownloadServices"
                [ngValue]="downloadService">{{downloadService.label}}</option>
      </select>
      <a class="btn btn-primary" [href]="currentDownloadService.url" target="_blank" [download]="currentDownloadService.filename" (click)="download(currentDownloadService)">Download</a>
    </div>
  `,
  styles: [
    `
    `
  ]
})
export class SpatialDownloadPanelComponent implements OnInit, OnChanges {
  @Input() downloadServices: DownloadService[] = [];
  @Input() currentBounds: Bounds;
  @Input() customBounds: Bounds = {
    north: -9,
    south: -44,
    east: 154,
    west: 112
  };
  @Input() title:string;
  @Input() subtitle:string;
  @Input() units:string;
  @Input() featureLayer:VectorLayerDescriptor;
  @Input() selectedFeature:any;
  @Input() parameters:any;
  @Input() wmsURL: string;
  @Input() wmsParameters:any;
  @Input() vectorLayer:string;
  @Output() boundsChanged = new EventEmitter<Bounds>();
  extentMode = 0;
  // imageURL='';
  activeDownloadServices: DownloadService[]
  currentDownloadService:DownloadService;
  effectiveBounds:Bounds;

  constructor() { }

  ngOnChanges(changes: SimpleChanges): void {
    this.initialiseDownloads();
  }

  ngOnInit(): void {
    this.initialiseDownloads();
  }

  mapBounds(): Bounds {
    if(this.extentMode===0){
      return this.currentBounds;
    }else if(this.extentMode===1){
      return getFeatureBounds(this.selectedFeature);
    }else{
      return this.customBounds;
    }
  }

  initialiseDownloads() {
    if(!this.wmsParameters){
      return;
    }
    this.effectiveBounds = this.mapBounds();
    this.emitBoundsEvent();
    if(!this.effectiveBounds){
      return;
    }
    // bounds = transformBounds(bounds,'EPSG:4326','EPSG:3857');
    // console.log(this.wmsURL,this.wmsParameters);
    const dynamicParameters:any = {};
    DYNAMIC_WMS_PARAMETERS.forEach(p=>{
      if(this.wmsParameters[p]){
        dynamicParameters[p]=this.wmsParameters[p];
      }
    });
    // const lonExtent = bounds.east-bounds.west;
    // const latExtent = bounds.north-bounds.south;
    // const aspect = lonExtent/latExtent;
    // const width = IMAGE_WIDTH;
    // const height = Math.round(width/aspect);

    // const requestParameters = {
    //   ...STATIC_WMS_PARAMETERS,
    //   ...dynamicParameters,
    //   srs:'EPSG:4326',
    //   bbox:boundsToBBox(bounds),
    //   width,
    //   height
    // }

    const availableParameters = {...this.parameters};
    if(this.selectedFeature){
      availableParameters.selectedFeature = this.selectedFeature;
      availableParameters.featureBounds = getFeatureBounds(this.selectedFeature);
    }

    const requestParameters = {
      ...this.effectiveBounds,
      ...dynamicParameters,
      title:this.title,
      subtitle:this.subtitle,
      units:this.units,
    }

    if(this.vectorLayer){
      requestParameters.vectorlayer = this.vectorLayer;
    }
    const defaultURLQuery = '?'+Object.keys(requestParameters).map(k=>`${k}=${encodeURIComponent(requestParameters[k])}`).join('&');
    this.downloadServices.forEach(ds=>{
      let urlQuery = defaultURLQuery;
      if(ds.parameters){
        urlQuery = this.makeCustomQueryString(ds.parameters,availableParameters);
      }

      if(urlQuery===null){
        ds.url = null;
        return;
      }

      ds.url = ds.baseURL+urlQuery;
      ds.filename = `download.${ds.extension}`;
    });

    this.activeDownloadServices = this.downloadServices.filter(ds=>ds.url);
    if(!this.currentDownloadService||!this.activeDownloadServices.includes(this.currentDownloadService)){
      this.currentDownloadService = this.activeDownloadServices[0];
    }

    // this.imageURL=url;
    // width=256
    // height=256
    // srs=EPSG%3A3857
    // bbox=15028131.257091936,5009377.085697314,20037508.342789244,10018754.171394628

  }

  customQueryParameter(param:string,viewParameters:any): string {
    let components = param.split('.');
    let value:any = viewParameters||{};
    for(let c of components){
      if(typeof(value[c])==='function'){
        value = value[c]();
      } else {
        value = value[c];
      }
      if(!value){
        return null;
      }
    }
    return ''+value;
  }

  makeCustomQueryString(parameters: {[key:string]:string},viewParameters:any): string {
    const keys = Object.keys(parameters);
    const values = keys.map(k=>this.customQueryParameter(parameters[k],viewParameters));
    if(values.some(v=>v===null)){
      return null;
    }
    return '?'+keys.map((k,i)=>`${k}=${encodeURIComponent(values[i])}`).join('&');
  }

  extentModeChanged(): void {
    this.initialiseDownloads();
  }

  emitBoundsEvent(): void {
    setTimeout(()=>{
      if(this.extentMode){
        this.boundsChanged.emit(this.effectiveBounds);
      } else {
        this.boundsChanged.emit(null);
      }
    });
  }
  customBoundsUpdated(bounds:Bounds): void {
    this.customBounds=bounds;
    this.initialiseDownloads();
  }

  download(downloadService:DownloadService):void {
    this.currentDownloadService=downloadService;
  }
}

function transformBounds(bounds: Bounds, srcProjection: string, destProjection: string): Bounds {
  const converter = (<any>proj4).default(srcProjection, destProjection);
  // const src = proj4.Proj(srcProjection);
  // const dest = proj4.Proj(destProjection);
  // const sw = proj4.transform(src, dest, [bounds.west, bounds.south]);
  // const ne = proj4.transform(src, dest, [bounds.east, bounds.north]);
  const sw = converter.forward([bounds.west, bounds.south]);
  const ne = converter.forward([bounds.east, bounds.north]);
  return {
    west: sw[0],
    south: sw[1],
    east: ne[0],
    north: ne[1]
  };
}
export function getFeatureBounds(selectedFeature: any): Bounds {
  if(!selectedFeature){
    return null;
  }
  const properties = selectedFeature.properties;
  if(properties?.maxx){
    return {
      west: properties.minx,
      south: properties.miny,
      east: properties.maxx,
      north: properties.maxy
    };
  }
  // compute bounds from geometry
  const geometry = selectedFeature.geometry;
  if(geometry.type==='Point'){
    return {
      west: geometry.coordinates[0],
      south: geometry.coordinates[1],
      east: geometry.coordinates[0],
      north: geometry.coordinates[1]
    };
  }
  if(geometry.type==='Polygon'){
    const coords:number[][] = geometry.coordinates[0];
    const west = Math.min(...coords.map(c=>c[0]));
    const south = Math.min(...coords.map(c=>c[1]));
    const east = Math.max(...coords.map(c=>c[0]));
    const north = Math.max(...coords.map(c=>c[1]));
    return {
      west,
      south,
      east,
      north
    };
  }

  return null;
}

